/**
 * 新規登録ページ - Nexus Core Design System (AIRGROW)
 * Step1: アカウント情報入力（氏名・メール）
 * Step2: パスワード設定
 * Step3: 利用形態選択（個人 or 組織）
 * Step4: メール確認コード
 * Step5: 登録完了
 */

import React, { useState } from 'react';
import { signUp, confirmSignUp } from '../services/auth';
import { Input } from '../components/ui';
import { useFadeNavigation } from '../hooks/useFadeNavigation';
import AuthLayout from '../components/auth/AuthLayout';

const STEPS = {
  ACCOUNT: 1,
  PASSWORD: 2,
  ORG_TYPE: 3,
  VERIFY: 4,
  COMPLETE: 5,
};

const MaterialIcon = ({ name, className = '', filled = false, style = {} }) => (
  <span
    className={`material-symbols-outlined ${className}`}
    style={{ fontVariationSettings: filled ? "'FILL' 1" : "'FILL' 0", ...style }}
  >
    {name}
  </span>
);

const VisibilityToggle = ({ visible, onToggle }) => (
  <button
    type="button"
    tabIndex={-1}
    onClick={onToggle}
    className="flex items-center text-outline hover:text-text-primary transition-colors focus:outline-none"
  >
    <MaterialIcon name={visible ? 'visibility_off' : 'visibility'} className="text-[18px]" />
  </button>
);

const STEP_LABELS = ['アカウント情報', 'パスワード', '利用形態', 'メール確認'];

function BrandHeader({ icon = 'rocket_launch', title, subtitle, iconBg = 'bg-nexus-primary', iconColor = 'text-white' }) {
  return (
    <div className="flex flex-col items-center mb-8">
      <div className={`w-12 h-12 ${iconBg} flex items-center justify-center rounded-lg mb-4 shadow-lg shadow-nexus-primary/20`}>
        <MaterialIcon name={icon} filled className={`${iconColor} text-[28px]`} />
      </div>
      <h1 className="text-[24px] leading-[32px] font-bold text-text-primary tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-text-secondary mt-1 text-center">{subtitle}</p>}
    </div>
  );
}

function ErrorAlert({ error }) {
  if (!error) return null;
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {error}
    </div>
  );
}

function OrgTypeTile({ value, icon, title, desc, selected, onSelect }) {
  return (
    <div
      className={`
        flex items-start gap-4 p-4 rounded-xl border cursor-pointer transition-colors
        ${selected ? 'border-nexus-primary bg-nexus-blue-soft/40' : 'border-border-light hover:bg-light-neutral'}
      `}
      onClick={() => onSelect(value)}
    >
      <div
        className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${
          selected ? 'bg-nexus-primary text-white' : 'bg-light-neutral text-outline'
        }`}
      >
        <MaterialIcon name={icon} className="text-[22px]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-text-primary text-sm">{title}</div>
        <div className="text-xs text-text-secondary mt-1 leading-relaxed">{desc}</div>
      </div>
      <div
        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
          selected ? 'border-nexus-primary bg-nexus-primary' : 'border-outline-variant'
        }`}
      >
        {selected && <div className="w-2 h-2 rounded-full bg-white" />}
      </div>
    </div>
  );
}

function StepIndicator({ step }) {
  return (
    <div className="flex items-center justify-center mb-8">
      {STEP_LABELS.map((label, i) => {
        const stepNum = i + 1;
        const isCompleted = step > stepNum;
        const isCurrent = step === stepNum;
        return (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center gap-1.5 min-w-[64px]">
              <div
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors
                  ${isCompleted ? 'bg-nexus-primary text-white' : ''}
                  ${isCurrent ? 'border-2 border-nexus-primary text-nexus-primary bg-white' : ''}
                  ${!isCompleted && !isCurrent ? 'bg-light-neutral text-outline-variant' : ''}
                `}
              >
                {isCompleted ? <MaterialIcon name="check" className="text-[18px]" /> : stepNum}
              </div>
              <span
                className={`text-[11px] ${
                  isCurrent ? 'text-nexus-primary font-medium' : 'text-text-secondary'
                }`}
              >
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-2 -mt-5 ${
                  step > stepNum ? 'bg-nexus-primary' : 'bg-border-light'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function Register() {
  const { cardVisible, isLeaving, navigateWithFade } = useFadeNavigation();
  const [step, setStep] = useState(STEPS.ACCOUNT);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    orgType: '',
    orgName: '',
    verificationCode: '',
  });

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const passwordChecks = {
    length: form.password.length >= 8,
    uppercase: /[A-Z]/.test(form.password),
    lowercase: /[a-z]/.test(form.password),
    number: /[0-9]/.test(form.password),
    match: form.password === form.confirmPassword && form.confirmPassword.length > 0,
  };
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);
  const isAccountInfoValid = Boolean(form.email && form.name);

  const handleAccountNext = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setError('すべての項目を入力してください');
      return;
    }
    setStep(STEPS.PASSWORD);
  };

  const handlePasswordNext = (e) => {
    e.preventDefault();
    if (!isPasswordValid) {
      setError('パスワードの要件を満たしてください');
      return;
    }
    setStep(STEPS.ORG_TYPE);
  };

  const handleOrgTypeNext = async (e) => {
    e.preventDefault();
    if (!form.orgType) {
      setError('利用形態を選択してください');
      return;
    }
    if (form.orgType === 'organization' && !form.orgName.trim()) {
      setError('組織名を入力してください');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await signUp({
        email: form.email,
        password: form.password,
        name: form.name,
        orgType: form.orgType,
      });
      setStep(STEPS.VERIFY);
    } catch (err) {
      if (err.code === 'UsernameExistsException') {
        setError('このメールアドレスはすでに登録されています');
      } else {
        setError(err.message || '登録に失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!form.verificationCode) {
      setError('確認コードを入力してください');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await confirmSignUp(form.email, form.verificationCode);
      sessionStorage.setItem(
        'pendingRegistration',
        JSON.stringify({
          name: form.name,
          orgType: form.orgType,
          orgName: form.orgType === 'organization' ? form.orgName : form.name,
        })
      );
      setStep(STEPS.COMPLETE);
    } catch (err) {
      if (err.code === 'CodeMismatchException') {
        setError('確認コードが正しくありません');
      } else if (err.code === 'ExpiredCodeException') {
        setError('確認コードの有効期限が切れています。再送信してください');
      } else {
        setError(err.message || '確認に失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setError('');
    try {
      const { resendConfirmationCode } = await import('../services/auth');
      await resendConfirmationCode(form.email);
      setError('');
      alert('確認コードを再送信しました');
    } catch (err) {
      setError('再送信に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // Step1: アカウント情報（氏名 + メール）
  if (step === STEPS.ACCOUNT) {
    return (
      <AuthLayout cardVisible={cardVisible}>
        <React.Fragment key={`step-${step}`}>
        <BrandHeader title="AIRGROW へようこそ" subtitle="新規アカウントを作成" />
        <StepIndicator step={step} />

        <form onSubmit={handleAccountNext} className="space-y-6">
          <ErrorAlert error={error} />

          <Input
            label="氏名"
            type="text"
            value={form.name}
            onChange={(e) => updateForm('name', e.target.value)}
            placeholder="例: 山田 太郎"
            required
            autoFocus
            icon={<MaterialIcon name="person" className="text-[18px]" />}
          />

          <Input
            label="メールアドレス"
            type="email"
            value={form.email}
            onChange={(e) => updateForm('email', e.target.value)}
            placeholder="name@company.com"
            required
            icon={<MaterialIcon name="mail" className="text-[18px]" />}
          />

          <button
            type="submit"
            disabled={!isAccountInfoValid || isLeaving}
            className="w-full flex justify-center items-center gap-2 py-3 px-4 bg-nexus-primary text-white text-sm font-semibold rounded-lg hover:bg-nexus-primary-container active:scale-[0.98] transition-all shadow-md shadow-nexus-primary/10 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            次へ
          </button>

          <div className="text-center text-xs">
            <span className="text-text-secondary">すでにアカウントをお持ちの方は </span>
            <a
              href="/login"
              onClick={(e) => {
                e.preventDefault();
                if (!isLeaving) navigateWithFade('/login');
              }}
              className="text-nexus-primary hover:underline font-medium"
            >
              ログイン
            </a>
          </div>
        </form>
        </React.Fragment>
      </AuthLayout>
    );
  }

  // Step2: パスワード設定
  if (step === STEPS.PASSWORD) {
    return (
      <AuthLayout cardVisible={cardVisible}>
        <React.Fragment key={`step-${step}`}>
        <BrandHeader icon="lock" title="パスワードを設定" subtitle="安全なパスワードを入力してください" />
        <StepIndicator step={step} />

        <form onSubmit={handlePasswordNext} className="space-y-6">
          <ErrorAlert error={error} />

          <Input
            label="パスワード"
            type={showPassword ? 'text' : 'password'}
            value={form.password}
            onChange={(e) => updateForm('password', e.target.value)}
            placeholder="8文字以上、大小英字・数字含む"
            required
            autoFocus
            icon={<MaterialIcon name="lock" className="text-[18px]" />}
            iconRight={<VisibilityToggle visible={showPassword} onToggle={() => setShowPassword((v) => !v)} />}
          />

          <Input
            label="パスワード確認"
            type={showConfirmPassword ? 'text' : 'password'}
            value={form.confirmPassword}
            onChange={(e) => updateForm('confirmPassword', e.target.value)}
            placeholder="パスワード再入力"
            required
            icon={<MaterialIcon name="lock" className="text-[18px]" />}
            iconRight={<VisibilityToggle visible={showConfirmPassword} onToggle={() => setShowConfirmPassword((v) => !v)} />}
          />

          {form.password && (
            <div className="bg-light-neutral rounded-lg p-3 space-y-1.5">
              <p className="text-xs font-medium text-text-secondary mb-2">パスワード要件:</p>
              {[
                { ok: passwordChecks.length, label: '8文字以上' },
                { ok: passwordChecks.uppercase, label: '大文字を含む (A-Z)' },
                { ok: passwordChecks.lowercase, label: '小文字を含む (a-z)' },
                { ok: passwordChecks.number, label: '数字を含む (0-9)' },
                { ok: passwordChecks.match, label: 'パスワードが一致' },
              ].map((c) => (
                <div
                  key={c.label}
                  className={`flex items-center gap-2 text-xs ${c.ok ? 'text-active-green' : 'text-outline'}`}
                >
                  <MaterialIcon
                    name={c.ok ? 'check_circle' : 'radio_button_unchecked'}
                    filled={c.ok}
                    className="text-[16px]"
                  />
                  {c.label}
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setStep(STEPS.ACCOUNT)}
              disabled={isLeaving}
              className="flex-1 flex justify-center items-center gap-2 py-3 px-4 bg-transparent text-text-primary border border-border-light text-sm font-semibold rounded-lg hover:bg-light-neutral active:scale-[0.98] transition-all disabled:opacity-60"
            >
              戻る
            </button>
            <button
              type="submit"
              disabled={!isPasswordValid || isLeaving}
              className="flex-1 flex justify-center items-center gap-2 py-3 px-4 bg-nexus-primary text-white text-sm font-semibold rounded-lg hover:bg-nexus-primary-container active:scale-[0.98] transition-all shadow-md shadow-nexus-primary/10 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              次へ
            </button>
          </div>
        </form>
        </React.Fragment>
      </AuthLayout>
    );
  }

  // Step3: 利用形態
  if (step === STEPS.ORG_TYPE) {
    return (
      <AuthLayout cardVisible={cardVisible}>
        <React.Fragment key={`step-${step}`}>
        <BrandHeader title="利用形態を選択" subtitle="後から変更することもできます" />
        <StepIndicator step={step} />

        <form onSubmit={handleOrgTypeNext} className="space-y-6">
          <ErrorAlert error={error} />

          <div className="space-y-3">
            <OrgTypeTile
              value="personal"
              icon="person"
              title="個人として使う"
              desc="自分だけでドローンを管理します。後から組織にアップグレードできます。"
              selected={form.orgType === 'personal'}
              onSelect={(v) => updateForm('orgType', v)}
            />
            <OrgTypeTile
              value="organization"
              icon="corporate_fare"
              title="組織として使う"
              desc="チームでドローンを管理します。メンバーを招待して共同管理できます。"
              selected={form.orgType === 'organization'}
              onSelect={(v) => updateForm('orgType', v)}
            />
          </div>

          {form.orgType === 'organization' && (
            <Input
              label="組織名"
              type="text"
              value={form.orgName}
              onChange={(e) => updateForm('orgName', e.target.value)}
              placeholder="例: 株式会社NTT e-Drone Technology"
              required
              autoFocus
              icon={<MaterialIcon name="corporate_fare" className="text-[18px]" />}
            />
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setStep(STEPS.PASSWORD)}
              disabled={isLoading || isLeaving}
              className="flex-1 flex justify-center items-center gap-2 py-3 px-4 bg-transparent text-text-primary border border-border-light text-sm font-semibold rounded-lg hover:bg-light-neutral active:scale-[0.98] transition-all disabled:opacity-60"
            >
              戻る
            </button>
            <button
              type="submit"
              disabled={isLoading || !form.orgType || isLeaving}
              className="flex-1 flex justify-center items-center gap-2 py-3 px-4 bg-nexus-primary text-white text-sm font-semibold rounded-lg hover:bg-nexus-primary-container active:scale-[0.98] transition-all shadow-md shadow-nexus-primary/10 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? '登録中...' : '登録する'}
            </button>
          </div>
        </form>
        </React.Fragment>
      </AuthLayout>
    );
  }

  // Step4: メール確認
  if (step === STEPS.VERIFY) {
    return (
      <AuthLayout cardVisible={cardVisible}>
        <React.Fragment key={`step-${step}`}>
        <BrandHeader
          icon="mark_email_unread"
          title="メールを確認してください"
          subtitle={
            <>
              <span className="font-medium text-text-primary">{form.email}</span> に確認コードを送信しました
            </>
          }
        />
        <StepIndicator step={step} />

        <form onSubmit={handleVerify} className="space-y-6">
          <ErrorAlert error={error} />

          <Input
            label="確認コード"
            type="text"
            value={form.verificationCode}
            onChange={(e) => updateForm('verificationCode', e.target.value)}
            placeholder="6桁のコードを入力"
            maxLength={6}
            required
            autoFocus
            className="text-center text-2xl tracking-widest"
          />

          <button
            type="submit"
            disabled={isLoading || isLeaving}
            className="w-full flex justify-center items-center gap-2 py-3 px-4 bg-nexus-primary text-white text-sm font-semibold rounded-lg hover:bg-nexus-primary-container active:scale-[0.98] transition-all shadow-md shadow-nexus-primary/10 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isLoading ? '確認中...' : '確認する'}
          </button>

          <div className="text-center text-xs">
            <span className="text-text-secondary">コードが届かない場合は </span>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isLoading}
              className="text-nexus-primary hover:underline font-medium disabled:opacity-60"
            >
              再送信
            </button>
          </div>
        </form>
        </React.Fragment>
      </AuthLayout>
    );
  }

  // Step5: 完了
  return (
    <AuthLayout cardVisible={cardVisible}>
      <React.Fragment key={`step-${step}`}>
      <div className="flex flex-col items-center text-center mb-6">
        <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mb-4">
          <MaterialIcon name="check_circle" filled className="text-active-green text-[36px]" />
        </div>
        <h1 className="text-[24px] leading-[32px] font-bold text-text-primary tracking-tight">登録完了</h1>
        <p className="text-sm text-text-secondary mt-2">
          {form.orgType === 'organization'
            ? `「${form.orgName}」として登録されました`
            : '個人アカウントが作成されました'}
        </p>
      </div>

      <div className="bg-light-neutral rounded-lg p-4 mb-6">
        <p className="text-sm text-text-primary">ログインしてドローン管理を始めましょう。</p>
        {form.orgType === 'personal' && (
          <p className="text-xs text-text-secondary mt-2 flex items-center gap-1">
            <MaterialIcon name="info" className="text-[16px]" />
            設定画面からいつでも組織にアップグレードできます
          </p>
        )}
      </div>

      <button
        type="button"
        disabled={isLeaving}
        onClick={() => navigateWithFade('/login')}
        className="w-full flex justify-center items-center gap-2 py-3 px-4 bg-nexus-primary text-white text-sm font-semibold rounded-lg hover:bg-nexus-primary-container active:scale-[0.98] transition-all shadow-md shadow-nexus-primary/10 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        ログインへ
      </button>
      </React.Fragment>
    </AuthLayout>
  );
}
